<template>
  <b-card id="descriptions-card" title="Descripcions">
    <!-- ACCOMMODATION DESCRIPTIONS -->
    <b-row>
      <b-col cols="12">
        <h5><feather-icon icon="HomeIcon" /> Allotjament</h5>
      </b-col>
      <b-col cols="12">
        <b-tabs no-fade>
          <template v-if="editMode" #tabs-end>
            <b-nav-item role="presentation">
              <b-button
                variant="primary"
                size="sm"
                @click.prevent="onNewAccommodationDescriptionButtonClicked"
              >
                <feather-icon icon="PlusIcon" />
                Nova <span class="d-none d-md-inline">descripció</span>
              </b-button>
            </b-nav-item>
          </template>
          <b-tab
            v-for="description in editedAccommodationDescriptions"
            :key="`accommodation-description-tab-${description.uuid}`"
            class="description-tab"
          >
            <template #title>
              <span>{{ getDescriptionLanguageCode(description) }}</span>
            </template>
            <description-form
              v-if="editMode"
              :description="description"
              accommodation-description
              @change="onAccommodationDescriptionChanged"
              @delete="onDeleteAccommodationDescriptionButtonClicked"
            />
            <div v-else class="description-text">
              <!-- eslint-disable-next-line vue/no-v-html -->
              <div v-html="description.content" />
            </div>
          </b-tab>
        </b-tabs>
      </b-col>
    </b-row>

    <!-- NO DESCRIPTIONS ALERT -->
    <b-alert
      class="mb-2"
      variant="primary"
      :show="!editMode && editedAccommodationDescriptions.length === 0"
    >
      <div class="alert-body">Encara no s'ha afegit cap descripció</div>
    </b-alert>

    <!-- BUTTONS -->
    <b-row class="mt-1 d-flex justify-content-between justify-content-sm-end">
      <b-col v-if="editMode" cols="6" sm="auto">
        <b-button block variant="light" @click="onCancelButtonClicked">
          Cancel·la
        </b-button>
      </b-col>
      <b-col v-if="editMode" cols="6" sm="auto">
        <b-button block variant="primary" @click="onSaveButtonClicked">
          Guarda
        </b-button>
      </b-col>
      <b-col v-if="!editMode" cols="12" sm="auto">
        <b-button block variant="primary" @click="onEditButtonClicked">
          Edita
        </b-button>
      </b-col>
    </b-row>
  </b-card>
</template>

<script>
import {
  BCard,
  BTabs,
  BTab,
  BRow,
  BCol,
  BButton,
  BNavItem,
  BAlert,
} from "bootstrap-vue";
import { notifySuccess, notifyError } from "@/utils/methods";
import { v4 as uuidv4 } from "uuid";
import DescriptionForm from "@/views/accommodations/accommodation/components/DescriptionForm.vue";

export default {
  components: {
    BCard,
    BTabs,
    BTab,
    BRow,
    BCol,
    BButton,
    BNavItem,
    DescriptionForm,
    BAlert,
  },
  data() {
    return {
      editMode: false,
      editedAccommodationDescriptions: [],
    };
  },
  computed: {
    accommodation() {
      return this.$store.getters["accommodation/accommodation"];
    },
    accommodationDescriptions() {
      return this.accommodation.descriptions
        ? this.accommodation.descriptions.filter(
            (d) => d.scope === "ACCOMMODATION"
          )
        : [];
    },
  },
  created() {
    this.initInputs();
  },
  watch: {
    accommodation() {
      this.initInputs();
    },
  },
  methods: {
    getDescriptionLanguageCode(description) {
      return description.language
        ? description.language.toUpperCase()
        : "Selecciona un idioma";
    },
    initInputs() {
      this.editedAccommodationDescriptions = [];
      if (this.accommodation.descriptions?.length > 0) {
        this.accommodation.descriptions.forEach((description) => {
          if (description.scope === "ACCOMMODATION") {
            this.editedAccommodationDescriptions.push({
              language: description.language,
              content: description.content,
              scope: description.scope,
              uuid: description.uuid,
              new: false,
            });
          }
        });
      }
    },
    onEditButtonClicked() {
      this.initInputs();
      this.editMode = true;
    },
    onCancelButtonClicked() {
      this.editMode = false;
      this.initInputs();
    },
    onSaveButtonClicked() {
      this.$store.dispatch("app/setLoading", true);

      // TODO: validate fields

      this.$store.dispatch("app/setLoading", true);
      this.$store
        .dispatch("accommodation/updateAccommodationDescriptions", {
          accommodation: this.accommodation,
          descriptions: [...this.editedAccommodationDescriptions],
        })
        .then(() => {
          notifySuccess(
            "Allotjament actualitzat",
            "L'allotjament s'ha actualitzat correctament"
          );
          this.editMode = false;
        })
        .catch(() =>
          notifyError(
            "Allotjament no actualitzat",
            "L'allotjament no s'ha pogut actualitzar correctament, si us plau, revisa les dades introduïdes"
          )
        )
        .finally(() => this.$store.dispatch("app/setLoading", false));
    },
    onNewAccommodationDescriptionButtonClicked() {
      this.editedAccommodationDescriptions.push({
        uuid: uuidv4(),
        language: null,
        content: null,
        scope: "ACCOMMODATION",
        new: true,
      });
    },
    onAccommodationDescriptionChanged(description) {
      const index = this.editedAccommodationDescriptions.findIndex(
        (d) => d.uuid === description.uuid
      );
      if (index !== -1)
        this.editedAccommodationDescriptions.splice(index, 1, description);
    },
    onDeleteAccommodationDescriptionButtonClicked(uuid) {
      const index = this.editedAccommodationDescriptions.findIndex(
        (d) => d.uuid === uuid
      );
      if (index !== -1) this.editedAccommodationDescriptions.splice(index, 1);
    },
  },
};
</script>

<style lang="scss">
#descriptions-card {
  .description-tab {
    .description-text {
      p {
        margin-top: 0;
        margin-bottom: 1rem;
      }
    }
  }
}
</style>
